import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, MessageCircle, Car, UtensilsCrossed, Loader2, ImagePlus, Ban, ShieldOff, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, EmptyState } from "../../components/app/ui";
import { useAuth } from "../../context/AuthContext";
import { chatApi, ridesApi, foodApi, restaurantApi, blockApi, errorMessage, titleCase } from "../../lib/api";

function timeOf(iso) { const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); }

export default function Chat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState(null);
  const [messages, setMessages] = useState([]);
  const [otherParties, setOtherParties] = useState([]);
  const [blocked, setBlocked] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  const fileRef = useRef(null);

  // Build the thread list from the unified inbox endpoint (active + past + support).
  useEffect(() => {
    let alive = true;
    (async () => {
      let list = [];
      try {
        const res = await chatApi.inbox();
        list = (res.threads || []).map((t) => ({ id: t.chat_id, title: t.title, subtitle: t.last_text || t.subtitle || "", unread: t.unread || 0, type: t.chat_type, status: t.status }));
      } catch { /* ignore */ }
      if (chatId && !list.some((t) => t.id === chatId)) {
        const type = chatId.startsWith("ord_") ? "order" : chatId.startsWith("sup_") ? "support" : chatId.startsWith("pcb_") ? "private_car" : "ride";
        list = [{ id: chatId, title: type === "support" ? "Support" : type === "order" ? "Order chat" : "Chat", subtitle: "", type }, ...list];
      }
      if (alive) setThreads(list);
    })();
    return () => { alive = false; };
  }, [user, chatId]);

  const load = useCallback(async () => {
    if (!chatId) return;
    try {
      const res = await chatApi.messages(chatId);
      setMessages(res.messages || []);
      setOtherParties(res.other_parties || []);
      setBlocked(!!res.blocked);
    } catch (e) { if (e?.response?.status !== 404) toast.error(errorMessage(e)); }
  }, [chatId]);

  const blockTarget = useMemo(() => (otherParties || []).find((p) => p.role !== "admin"), [otherParties]);

  const toggleBlock = async () => {
    if (!blockTarget) return;
    setBusy(true);
    try {
      if (blockTarget.blocked_by_me) {
        await blockApi.unblock(blockTarget.user_id);
        toast.success(`Unblocked ${blockTarget.name || "user"}`);
      } else {
        await blockApi.block({ user_id: blockTarget.user_id });
        toast.success(`Blocked ${blockTarget.name || "user"}. They can no longer message you.`);
      }
      await load();
    } catch (e) { toast.error(errorMessage(e, "Could not update block")); } finally { setBusy(false); }
  };

  useEffect(() => { setMessages([]); load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !chatId) return;
    setBusy(true);
    try { const m = await chatApi.send(chatId, text.trim()); setMessages((prev) => [...prev, m]); setText(""); } catch (err) { toast.error(errorMessage(err, "Could not send")); } finally { setBusy(false); }
  };

  const sendImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !chatId) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image");
    if (f.size > 10 * 1024 * 1024) return toast.error("Image must be under 10 MB");
    setBusy(true);
    try {
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const base64 = String(dataUrl).split(",")[1] || "";
      const m = await chatApi.send(chatId, "", { file_data: base64, mime: f.type });
      setMessages((prev) => [...prev, m]);
    } catch (err) { toast.error(errorMessage(err, "Could not send photo")); } finally { setBusy(false); }
  };

  const active = useMemo(() => threads?.find((t) => t.id === chatId), [threads, chatId]);

  return (
    <>
      <PageHeader title="Messages" description="Chat with drivers, restaurants, customers — and reach our support team anytime." />
      <div className="kt-card grid min-h-[560px] overflow-hidden lg:grid-cols-[320px_1fr]" data-testid="chat-layout">
        <aside className={`border-surface-200 lg:border-r ${chatId ? "hidden lg:block" : ""}`}>
          <div className="border-b border-surface-200 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">Conversations</div>
          <ul className="max-h-[600px] overflow-y-auto kt-scrollbar" data-testid="chat-threads">
            {threads === null && <li className="p-4 text-sm text-ink-muted">Loading…</li>}
            {threads?.length === 0 && <li className="p-6 text-center text-sm text-ink-muted">No conversations yet.</li>}
            {threads?.map((t) => (
              <li key={t.id}>
                <button onClick={() => navigate(`/app/chat/${t.id}`)} className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-50 ${t.id === chatId ? "bg-brand-50" : ""}`} data-testid={`chat-thread-${t.id}`}>
                  <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.type === "support" ? "bg-rose-50 text-rose-600" : t.type === "order" || t.type === "food" ? "bg-brand-50 text-brand" : "bg-graphite-50 text-graphite"}`}>{t.type === "support" ? <LifeBuoy className="h-4 w-4" /> : (t.type === "order" || t.type === "food") ? <UtensilsCrossed className="h-4 w-4" /> : <Car className="h-4 w-4" />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2"><span className="truncate text-sm font-semibold text-ink">{t.title}</span>{t.status && <span className="shrink-0 text-[10px] uppercase text-ink-muted">{titleCase(t.status)}</span>}</span>
                    <span className="block truncate text-xs text-ink-soft">{t.subtitle}</span>
                  </span>
                  {t.unread > 0 && <span className="rounded-full bg-brand px-1.5 text-[10px] font-bold text-white">{t.unread}</span>}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section className="flex flex-col">
          {!chatId ? (
            <div className="flex flex-1 items-center justify-center p-8"><EmptyState icon={MessageCircle} title="Select a conversation" text="Pick a ride or order from the left to view messages." /></div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-surface-200 px-5 py-3">
                <button onClick={() => navigate("/app/chat")} className="text-sm text-ink-soft lg:hidden">Back</button>
                <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-ink" data-testid="chat-title">{active?.title || "Conversation"}</p><p className="truncate text-xs text-ink-muted">{active?.subtitle || chatId}</p></div>
                {blockTarget && (
                  <button
                    onClick={toggleBlock}
                    disabled={busy}
                    data-testid="chat-block-toggle"
                    title={blockTarget.blocked_by_me ? "Unblock user" : "Block user"}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${blockTarget.blocked_by_me ? "border-surface-300 text-ink-soft hover:bg-surface-100" : "border-red-200 text-red-600 hover:bg-red-50"}`}
                  >
                    {blockTarget.blocked_by_me ? <><ShieldOff className="h-3.5 w-3.5" /> Unblock</> : <><Ban className="h-3.5 w-3.5" /> Block</>}
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto bg-surface-50/60 p-5 kt-scrollbar" style={{ maxHeight: 460 }} data-testid="chat-messages">
                {messages.length === 0 && <p className="py-10 text-center text-sm text-ink-muted">No messages yet. Say hello.</p>}
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${mine ? "rounded-br-md bg-brand text-white" : "rounded-bl-md bg-white text-ink"}`}>
                        {!mine && <p className="mb-0.5 text-[11px] font-semibold text-ink-muted">{m.sender_label || `${m.sender_name} · ${titleCase(m.sender_role || "")}`}</p>}
                        {m.image_id && <a href={chatApi.imageUrl(chatId, m.image_id)} target="_blank" rel="noreferrer"><img src={chatApi.imageUrl(chatId, m.image_id)} alt="attachment" className="mb-1 max-h-60 rounded-lg object-cover" data-testid={`chat-image-${m.message_id}`} /></a>}
                        {m.text ? <p className="whitespace-pre-wrap">{m.text}</p> : null}
                        <p className={`mt-1 text-right text-[10px] ${mine ? "text-white/70" : "text-ink-muted"}`}>{timeOf(m.sent_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottom} />
              </div>
              {blocked ? (
                <div className="flex items-center justify-center gap-2 border-t border-surface-200 bg-surface-50 p-4 text-center text-sm text-ink-muted" data-testid="chat-blocked-note">
                  <Ban className="h-4 w-4 text-red-500" /> Messaging is unavailable because {blockTarget?.blocked_by_me ? "you blocked this user" : "one of you blocked the other"}.
                  {blockTarget?.blocked_by_me && <button onClick={toggleBlock} disabled={busy} className="font-semibold text-brand hover:underline" data-testid="chat-blocked-unblock">Unblock</button>}
                </div>
              ) : (
                <form onSubmit={send} className="flex items-center gap-2 border-t border-surface-200 p-3">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid="chat-image-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="h-11 w-11 shrink-0 rounded-full p-0" data-testid="chat-attach"><ImagePlus className="h-4 w-4" /></Button>
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="h-11 flex-1 bg-white" data-testid="chat-input" />
                  <Button type="submit" disabled={busy || !text.trim()} className="h-11 rounded-full bg-brand px-5 text-white hover:bg-brand-600" data-testid="chat-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
                </form>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}
